import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Layout } from '@roc/client-portal-shared/components';
import {
  AutocompleteAddressField,
  Button,
  createSelectField,
  createTextField,
  Paper,
} from '@roc/ui';
import { stateOptions } from '@roc/ui/formComponents';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { TitleHistoryLookupStore } from '../stores/titleHistoryLookupStore';
import { TitleHistoryLookupGrid } from './titleHistoryLookupGrid';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  gridContainer: {
    marginTop: theme.spacing(2),
  },
}));

interface TitleHistoryLookupProps {
  store: TitleHistoryLookupStore;
}

export const TitleHistoryLookup = observer(({ store }: TitleHistoryLookupProps) => {
  const classes = useStyles();

  useEffect(() => {
    return () => store.resetStore();
  }, [store]);

  return (
    <Layout title="Title History Lookup" maxWidth={'xl'}>
      <Paper className={classes.paper}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant={'h5'}>Property Address</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <AddressLine1 store={store} />
          </Grid>
          <Grid item xs={12} md={6}>
            {createTextField({
              store,
              label: 'Address Line 2',
              fieldName: 'aptNumber',
              testId: 'aptNumber',
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {createSelectField({
              store,
              label: 'State',
              fieldName: 'state',
              testId: 'state',
              options: stateOptions,
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {createTextField({
              store,
              label: 'City',
              fieldName: 'city',
              testId: 'city',
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {createTextField({
              store,
              label: 'Zip Code',
              fieldName: 'zipCode',
              testId: 'zipCode',
            })}
          </Grid>
          <Grid item container xs={12} justifyContent="flex-end">
            <Grid item xs={12} md={3}>
              <Button
                testId="getTitleHistory"
                color="primary"
                variant="contained"
                size="large"
                fullWidth
                disabled={store.isLoading}
                onClick={() => store.lookupTitleHistory()}
              >
                Get Title History
              </Button>
            </Grid>
          </Grid>
        </Grid>
        {(store.hasSearched || store.isLoading) && (
          <Grid container className={classes.gridContainer}>
            <Grid item xs={12}>
              <TitleHistoryLookupGrid rows={store.rows} isLoading={store.isLoading} />
            </Grid>
          </Grid>
        )}
      </Paper>
    </Layout>
  );
});

const AddressLine1 = observer(({ store }: TitleHistoryLookupProps) => {
  const { address } = store.form.fields;

  const handleChange = event => {
    const { value, fullInfo } = event?.target || {};
    const { address_components, geometry } = fullInfo || {};
    store.handleAddressChange(value, address_components, geometry);
  };

  return (
    <AutocompleteAddressField
      label="Address Line 1"
      name="address"
      value={address.value}
      onChange={handleChange}
      fullWidth
      testId="address"
      standaloneLabel
    />
  );
});
